<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { printDiscountAction } from '@/api/functions/discounts/get-all-discounts-action'
import { getDiscountsAction } from '@/api/functions/settings/discounts-action'
import type { ApiPaginationResponse } from '@/api/typings/response.typing'
import type { DiscountItem } from '@/api/typings/settings.typing'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useGeneralSettings } from '@/composables/useGeneralSettings'
import { useToast } from '@/composables/useToast'
import { apiFileUrl } from '@/configs/urls'
import { settingsTabs } from '@/constants/setting-tabs.ts'
import { getErrorMessage } from '@/utils/errorUtils'
import SettingsOverview from '../partials/SettingsOverview.vue'
import CreateDiscountModal from './partials/CreateDiscountModal.vue'
import DiscountCard from './partials/DiscountCard.vue'

const router = useRouter()
const route = useRoute()
const toast = useToast()
const showCreateModal = ref(false)
const currentPage = ref(1)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const activeFilters = ref<Record<string, any>>({})

//  Filter Fields
const filterFields = computed(() => [
  {
    type: 'input',
    key: 'id',
    label: 'کد تخفیف را وارد کنید',
    title: 'جستجو',
  },
  {
    type: 'date',
    key: 'discountDate',
    title: 'تاریخ',
    rangeKeys: {
      min: 'from_date',
      max: 'till_date',
    },
  },
  {
    type: 'select',
    key: 'used',
    label: 'انتخاب کنید',
    title: 'نوع استفاده',
    options: [
      { label: 'تخفیف‌های استفاده شده', value: 'true' },
      { label: 'تخفیف‌های استفاده نشده', value: 'false' },
    ],
  },
  {
    type: 'select',
    key: 'Type',
    label: 'انتخاب کنید',
    title: 'نوع تخفیف',
    options: [
      { label: 'ادمین', value: 'ADMIN' },
      { label: 'سیستمی', value: 'SYSTEM' },
    ],
  },
])

const { currencies, discountTypeOptions } = useGeneralSettings()

const {
  data: discountsData,
  refetch,
  isLoading,
} = useQueryApi<ApiPaginationResponse<DiscountItem[]>>({
  queryKey: computed(() => ['discounts', currentPage.value, activeFilters.value]),
  queryFn: () => {
    const filters = Object.fromEntries(
      Object.entries(activeFilters.value).filter(([key, value]) => key !== 'page' && value !== false)
    )

    return getDiscountsAction({
      page: currentPage.value,
      ...filters,
    })
  },
  // keepPreviousData: true,

  staleTime: 1000 * 60 * 3,
})

const discounts = computed(() => discountsData.value?.data || [])
const totalItems = computed(() => discountsData.value?.pagination?.total ?? 0)
const itemsPerPage = computed(() => discountsData.value?.pagination?.perPage ?? 10)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onApplyFilters = (values: Record<string, any>) => {
  activeFilters.value = { ...values }
  currentPage.value = 1
  router.push({ path: route.path, query: Object.fromEntries(Object.entries(values).filter(([_, v]) => v)) })
  refetch()
}

const onResetFilters = () => {
  activeFilters.value = {}
  currentPage.value = 1
  router.push({ path: route.path, query: {} })
  refetch()
}

const goToPage = (page: number) => {
  currentPage.value = page
  refetch()
}

function resetFiltersAndReload() {
  activeFilters.value = {}
  goToPage(1)
  router.push({ path: '/settings/discounts', query: {} })
}

const printDiscountsMutation = useMutationApi({
  mutationFn: () => printDiscountAction(),
  onSuccess: (res) => {
    const pdfPath = res?.data.pdf_path
    if (pdfPath) {
      window.open(apiFileUrl(pdfPath), '_blank')
    } else {
      toast.error('PDF یافت نشد')
    }
  },
  onError: (err: unknown) => {
    toast.error(getErrorMessage(err))
  },
})

const handlePrintDiscounts = () => {
  printDiscountsMutation.mutate()
}

onMounted(() => {
  if (Object.keys(route.query).length > 0) {
    const { page, ...filters } = route.query

    activeFilters.value = filters

    if (page) {
      currentPage.value = Number(page)
    }
  }
})
</script>

<template>
  <div class="w-full mb-12">
    <CreateDiscountModal v-model="showCreateModal" :currencies="currencies" :discountTypeOptions="discountTypeOptions"
      @confirm="refetch()" @cancel="showCreateModal = false" />

    <SettingsOverview :tabs="settingsTabs">
      <template #actions>
        <div class="flex lg:flex-col-reverse items-center gap-2 pb-1">
          <Button :isAdmin="true" @click="showCreateModal = true"
            class="px-3! rounded-lg! h-8! py-0 text-xs font-medium">
            ایجاد تخفیف جدید
          </Button>
          <Button @click="handlePrintDiscounts" :isAdmin="true" variant="outline"
            class="px-3! rounded-lg! h-8! border-2 py-0 text-xs font-medium">
            <span class="flex items-center justify-center gap-2 ">
              <img width="19" src="/svg/print-svgrepo-com.svg" />
              چاپ گزارش
            </span>
          </Button>
        </div>
      </template>
    </SettingsOverview>

    <!--  Filters Section -->
    <Filters :fields="filterFields" @apply="onApplyFilters" @reset="onResetFilters" class="mt-20 lg:mt-0" />


    <!-- Main Cards && Pagination  -->
    <div v-if="isLoading" class="text-center py-8 text-gray-500">
      در حال دریافت اطلاعات...
    </div>
    <div v-else>
      <div v-if="discounts.length > 0" class="mt-8 flex flex-col gap-6 ">
        <DiscountCard v-for="(discount, index) in discounts" :key="`card-item-${index}`" :currencies="currencies"
          :discount="discount" :discountTypeOptions="discountTypeOptions" @confirmDiscount="refetch()" />

        <!-- Pagination -->
        <Pagination v-model:currentPage="currentPage" :itemsPerPage="itemsPerPage" :totalItems="totalItems"
          @update:currentPage="goToPage" :isAdmin="true" />
      </div>
      <EmptyState v-else :onReset="resetFiltersAndReload" />
    </div>
  </div>
</template>
